import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  MultiSelect,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  type HighlightSegment,
  type OverlayTrackLayer,
  type Tack,
  type TrackPoint,
} from '../../types';
import { distanciaPuntos } from '../../utils/geo';
import { formatMetricLabel, formatMetricLabelNoUnit } from '../../utils/metricLabels';

import './LineupPanel.css';
import './themeInversion.css';

const PRIMARY_COLOR = 'var(--lineup-primary-color, #2563eb)';
const SECONDARY_COLOR = 'var(--lineup-secondary-color, #f97316)';
const RESERVED_METRIC_KEYS = new Set(['utc', 'lat', 'lon', 'globalIndex']);
const DEFAULT_METRICS = [
  'tws',
  'twd',
  'bsp',
  'bsppc',
  'sog',
  'vmg',
  'vmgpc',
  'heel',
  'twa',
  'twa_abs',
  'rudder',
  'trim',
  'rot',
  'hdg',
];
const MANEUVER_PADDING_SECONDS = 5;
const MANEUVER_PADDING_MS = MANEUVER_PADDING_SECONDS * 1000;

interface TimeWindow {
  startMs: number;
  endMs: number;
}
interface IndexRange {
  start: number;
  end: number;
}
interface Sample {
  point: TrackPoint;
  index?: number;
  timestamp: number;
  utc: string;
}
interface NumericSample {
  value: number;
  sample: Sample;
}
interface MetricStats {
  metric: string;
  count: number;
  avg: number | null;
  min: number | null;
  max: number | null;
  stdDev: number | null;
  minSample?: NumericSample;
  maxSample?: NumericSample;
}
interface BoatSummary {
  id: string;
  label: string;
  color: string;
  samples: Sample[];
  stats: Record<string, MetricStats>;
  distanceSailed: number;
  straightLineDistance: number;
  durationMs: number;
  avgHeading: number | null;
  avgTwd: number | null;
}
interface RangeGroup {
  id: string;
  label: string;
  description: string;
  durationLabel: string;
  indexRanges: IndexRange[];
  timeWindows: TimeWindow[];
}
interface GainEntry {
  begin: number | null;
  end: number | null;
  total: number | null;
  perMinute: number | null;
}
interface GainSummary {
  forward: GainEntry;
  up: GainEntry;
  vmg: GainEntry;
}

export interface LineupPanelProperties {
  trackData: TrackPoint[];
  highlightSegments: HighlightSegment[];
  relatedTrackLayers: OverlayTrackLayer[];
  tacks: Tack[];
  primaryBoatName?: string | null;
  globalToRelativeIndexMap?: Record<number, number>;
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const asPointRecord = (point: TrackPoint): Record<string, unknown> =>
  point as unknown as Record<string, unknown>;

function listNumericMetrics(trackData: TrackPoint[]): string[] {
  const metrics = new Set<string>();
  for (const point of trackData) {
    for (const [key, raw] of Object.entries(asPointRecord(point))) {
      if (RESERVED_METRIC_KEYS.has(key)) {
        continue;
      }
      if (coerceNumber(raw) !== null) {
        metrics.add(key);
      }
    }
  }
  return [...metrics].sort((a, b) => a.localeCompare(b));
}

function mergeWindows(windows: TimeWindow[]): TimeWindow[] {
  if (windows.length === 0) {
    return [];
  }
  const sorted = [...windows].sort((a, b) => a.startMs - b.startMs);
  const merged: TimeWindow[] = [sorted[0]];
  for (const current of sorted.slice(1)) {
    const last = merged.at(-1);
    if (!last) {
      merged.push({ ...current });
      continue;
    }
    if (current.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, current.endMs);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

function timestampWithinAnyWindow(timestamp: number, windows: TimeWindow[]): boolean {
  if (windows.length === 0) {
    return true;
  }
  for (const window of windows) {
    if (timestamp >= window.startMs && timestamp <= window.endMs) {
      return true;
    }
  }
  return false;
}

function collectPrimarySamples(
  trackData: TrackPoint[],
  ranges: IndexRange[],
  blockWindows: TimeWindow[],
): Sample[] {
  const samples: Sample[] = [];
  for (const range of ranges) {
    for (let index = range.start; index <= range.end; index += 1) {
      const point = trackData[index];
      if (!point?.utc) {
        continue;
      }
      const timestamp = Date.parse(point.utc);
      if (!Number.isFinite(timestamp)) {
        continue;
      }
      if (blockWindows.length > 0 && timestampWithinAnyWindow(timestamp, blockWindows)) {
        continue;
      }
      samples.push({ point, index, timestamp, utc: point.utc });
    }
  }
  return samples;
}

function collectOverlaySamples(
  trackData: TrackPoint[],
  windows: TimeWindow[],
  blockWindows: TimeWindow[],
): Sample[] {
  if (windows.length === 0) {
    return [];
  }
  const mergedWindows = mergeWindows(windows);
  const samples: Sample[] = [];
  for (const point of trackData) {
    if (!point?.utc) {
      continue;
    }
    const timestamp = Date.parse(point.utc);
    if (!Number.isFinite(timestamp)) {
      continue;
    }
    if (!timestampWithinAnyWindow(timestamp, mergedWindows)) {
      continue;
    }
    if (blockWindows.length > 0 && timestampWithinAnyWindow(timestamp, blockWindows)) {
      continue;
    }
    samples.push({ point, timestamp, utc: point.utc });
  }
  if (samples.length === 0) {
    for (const point of trackData) {
      if (!point?.utc) {
        continue;
      }
      const timestamp = Date.parse(point.utc);
      if (!Number.isFinite(timestamp)) {
        continue;
      }
      if (blockWindows.length > 0 && timestampWithinAnyWindow(timestamp, blockWindows)) {
        continue;
      }
      samples.push({ point, timestamp, utc: point.utc });
    }
  }
  return samples;
}

function computeMetricStats(metric: string, samples: Sample[]): MetricStats {
  const numericSamples: NumericSample[] = [];
  for (const sample of samples) {
    const value = coerceNumber(asPointRecord(sample.point)[metric]);
    if (value === null) {
      continue;
    }
    numericSamples.push({ value, sample });
  }
  if (numericSamples.length === 0) {
    return { metric, count: 0, avg: null, min: null, max: null, stdDev: null };
  }
  let sum = 0;
  let minSample = numericSamples[0];
  let maxSample = numericSamples[0];
  for (const entry of numericSamples) {
    sum += entry.value;
    if (entry.value < minSample.value) {
      minSample = entry;
    }
    if (entry.value > maxSample.value) {
      maxSample = entry;
    }
  }
  const mean = sum / numericSamples.length;
  let variance = 0;
  if (numericSamples.length >= 2) {
    for (const entry of numericSamples) {
      const diff = entry.value - mean;
      variance += diff * diff;
    }
    variance /= numericSamples.length - 1;
  }
  const stdDev = numericSamples.length >= 2 ? Math.sqrt(variance) : null;
  return {
    metric,
    count: numericSamples.length,
    avg: mean,
    min: minSample.value,
    max: maxSample.value,
    stdDev,
    minSample,
    maxSample,
  };
}

function computeTrackDistance(samples: Sample[]): number {
  if (samples.length < 2) {
    return 0;
  }
  let total = 0;
  for (let index = 1; index < samples.length; index += 1) {
    const previous = samples[index - 1].point;
    const current = samples[index].point;
    if (
      typeof previous.lat === 'number' &&
      typeof previous.lon === 'number' &&
      typeof current.lat === 'number' &&
      typeof current.lon === 'number'
    ) {
      total += distanciaPuntos(previous.lat, previous.lon, current.lat, current.lon);
    }
  }
  return total;
}

function computeStraightLine(samples: Sample[]): number {
  if (samples.length < 2) {
    return 0;
  }
  const start = samples[0].point;
  const endSample = samples.at(-1);
  if (!endSample) {
    return 0;
  }
  const end = endSample.point;
  if (
    typeof start.lat !== 'number' ||
    typeof start.lon !== 'number' ||
    typeof end.lat !== 'number' ||
    typeof end.lon !== 'number'
  ) {
    return 0;
  }
  return distanciaPuntos(start.lat, start.lon, end.lat, end.lon);
}

function averageAngle(samples: Sample[], key: 'hdg' | 'cog' | 'twd'): number | null {
  let sinSum = 0;
  let cosSum = 0;
  let count = 0;
  for (const sample of samples) {
    const value = coerceNumber(asPointRecord(sample.point)[key]);
    if (value === null) {
      continue;
    }
    const radians = (value * Math.PI) / 180;
    sinSum += Math.sin(radians);
    cosSum += Math.cos(radians);
    count += 1;
  }
  if (count === 0) {
    return null;
  }
  const angle = Math.atan2(sinSum / count, cosSum / count);
  return ((angle * 180) / Math.PI + 360) % 360;
}

function angleToVector(angle: number | null): { x: number; y: number } | null {
  if (angle === null || Number.isNaN(angle)) {
    return null;
  }
  const radians = (angle * Math.PI) / 180;
  return { x: Math.sin(radians), y: Math.cos(radians) };
}

function rotate90(vector: { x: number; y: number } | null): { x: number; y: number } | null {
  if (!vector) {
    return null;
  }
  return { x: vector.y, y: -vector.x };
}

function projectPoint(
  point: TrackPoint,
  origin: { lat: number; lon: number },
): { x: number; y: number } | null {
  if (typeof point.lat !== 'number' || typeof point.lon !== 'number') {
    return null;
  }
  const earthRadius = 6_371_000;
  const latRad = (point.lat * Math.PI) / 180;
  const lonRad = (point.lon * Math.PI) / 180;
  const originLatRad = (origin.lat * Math.PI) / 180;
  const originLonRad = (origin.lon * Math.PI) / 180;
  const x = (lonRad - originLonRad) * Math.cos((origin.lat * Math.PI) / 180) * earthRadius;
  const y = (latRad - originLatRad) * earthRadius;
  return { x, y };
}

function computeGainSummary(primary: BoatSummary, comparison: BoatSummary): GainSummary | null {
  if (primary.samples.length === 0 || comparison.samples.length === 0) {
    return null;
  }
  const primaryStart = primary.samples[0].point;
  const primaryEndSample = primary.samples.at(-1);
  const comparisonStart = comparison.samples[0].point;
  const comparisonEndSample = comparison.samples.at(-1);
  if (!primaryEndSample || !comparisonEndSample) {
    return null;
  }
  const primaryEnd = primaryEndSample.point;
  const comparisonEnd = comparisonEndSample.point;
  if (
    typeof primaryStart.lat !== 'number' ||
    typeof primaryStart.lon !== 'number' ||
    typeof primaryEnd.lat !== 'number' ||
    typeof primaryEnd.lon !== 'number' ||
    typeof comparisonStart.lat !== 'number' ||
    typeof comparisonStart.lon !== 'number' ||
    typeof comparisonEnd.lat !== 'number' ||
    typeof comparisonEnd.lon !== 'number'
  ) {
    return null;
  }
  const origin = {
    lat: (primaryStart.lat + comparisonStart.lat) / 2,
    lon: (primaryStart.lon + comparisonStart.lon) / 2,
  };
  const primaryStartVec = projectPoint(primaryStart, origin);
  const primaryEndVec = projectPoint(primaryEnd, origin);
  const comparisonStartVec = projectPoint(comparisonStart, origin);
  const comparisonEndVec = projectPoint(comparisonEnd, origin);
  if (!primaryStartVec || !primaryEndVec || !comparisonStartVec || !comparisonEndVec) {
    return null;
  }
  const relativeStart = {
    x: primaryStartVec.x - comparisonStartVec.x,
    y: primaryStartVec.y - comparisonStartVec.y,
  };
  const relativeEnd = {
    x: primaryEndVec.x - comparisonEndVec.x,
    y: primaryEndVec.y - comparisonEndVec.y,
  };
  const forwardAxis = angleToVector(primary.avgHeading ?? comparison.avgHeading);
  const upAxis = rotate90(forwardAxis);
  const vmgAxis = angleToVector(primary.avgTwd ?? comparison.avgTwd ?? primary.avgHeading ?? null);
  const durationMinutes = Math.max(primary.durationMs, comparison.durationMs) / 60_000;
  const project = (axis: { x: number; y: number } | null): GainEntry => {
    if (!axis) {
      return { begin: null, end: null, total: null, perMinute: null };
    }
    const begin = relativeStart.x * axis.x + relativeStart.y * axis.y;
    const end = relativeEnd.x * axis.x + relativeEnd.y * axis.y;
    const total = end - begin;
    const perMinute = durationMinutes > 0 ? total / durationMinutes : null;
    return { begin, end, total, perMinute };
  };
  return {
    forward: project(forwardAxis),
    up: project(upAxis),
    vmg: project(vmgAxis),
  };
}

function formatDurationLabel(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return '0s';
  }
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  }
  return `${seconds}s`;
}

function formatTimestampLabel(timestamp: number, locale: string): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return formatter.format(timestamp);
}

export const LineupPanel: React.FC<LineupPanelProperties> = ({
  trackData,
  highlightSegments,
  relatedTrackLayers,
  tacks,
  primaryBoatName,
  globalToRelativeIndexMap,
}) => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language;
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_METRICS);
  const [selectedComparisonKey, setSelectedComparisonKey] = useState<string | null>(null);
  const [excludeManeuvers, setExcludeManeuvers] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const translateGlobalIndex = useCallback(
    (index?: number | null): number | null => {
      if (typeof index !== 'number' || Number.isNaN(index)) {
        return null;
      }
      if (globalToRelativeIndexMap && globalToRelativeIndexMap[index] !== undefined) {
        return globalToRelativeIndexMap[index] ?? null;
      }
      return index;
    },
    [globalToRelativeIndexMap],
  );

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }),
    [locale],
  );
  const signedFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1, signDisplay: 'always' }),
    [locale],
  );
  const distanceFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
    [locale],
  );

  const availableMetrics = useMemo(() => listNumericMetrics(trackData), [trackData]);

  useEffect(() => {
    setSelectedMetrics((previous) => {
      const filtered = previous.filter((metric) => availableMetrics.includes(metric));
      if (filtered.length > 0) {
        return filtered;
      }
      const fallback = DEFAULT_METRICS.filter((metric) => availableMetrics.includes(metric)).slice(0, 8);
      if (fallback.length > 0) {
        return fallback;
      }
      return availableMetrics.slice(0, 8);
    });
  }, [availableMetrics]);

  const highlightRanges = useMemo<IndexRange[]>(() => {
    const ranges: IndexRange[] = [];
    for (const segment of highlightSegments) {
      if (segment.endIndex == null) {
        continue;
      }
      const start = Math.max(0, Math.min(segment.startIndex, segment.endIndex));
      const end = Math.min(trackData.length - 1, Math.max(segment.startIndex, segment.endIndex));
      if (start < end) {
        ranges.push({ start, end });
      }
    }
    if (ranges.length === 0 && trackData.length > 0) {
      ranges.push({ start: 0, end: trackData.length - 1 });
    }
    ranges.sort((a, b) => a.start - b.start);
    return ranges;
  }, [highlightSegments, trackData.length]);

  const rangeGroups = useMemo<RangeGroup[]>(() => {
    if (trackData.length === 0 || highlightRanges.length === 0) {
      return [];
    }
    const buildGroup = (id: string, ranges: IndexRange[], label: string): RangeGroup => {
      const first = trackData[ranges[0].start];
      const lastRange = ranges.at(-1);
      const last = lastRange ? trackData[lastRange.end] : undefined;
      const startTimestamp = first?.utc ? Date.parse(first.utc) : Number.NaN;
      const endTimestamp = last?.utc ? Date.parse(last.utc) : Number.NaN;
      const duration = Number.isFinite(startTimestamp) && Number.isFinite(endTimestamp)
        ? Math.max(0, endTimestamp - startTimestamp)
        : 0;
      const description =
        Number.isFinite(startTimestamp) && Number.isFinite(endTimestamp)
          ? t('race.lineup.rangeDescription', {
              start: formatTimestampLabel(startTimestamp, locale),
              end: formatTimestampLabel(endTimestamp, locale),
              duration: formatDurationLabel(duration),
            })
          : t('race.lineup.rangeUnknown', 'Waiting for timestamps');
      const timeWindows: TimeWindow[] = [];
      for (const range of ranges) {
        const start = trackData[range.start];
        const end = trackData[range.end];
        if (!start?.utc || !end?.utc) {
          continue;
        }
        const startMs = Date.parse(start.utc);
        const endMs = Date.parse(end.utc);
        if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
          timeWindows.push({ startMs, endMs });
        }
      }
      return {
        id,
        label,
        description,
        durationLabel: formatDurationLabel(duration),
        indexRanges: ranges,
        timeWindows: mergeWindows(timeWindows),
      };
    };

    if (highlightRanges.length === 0) {
      return [];
    }
    return [
      buildGroup(
        'combined',
        highlightRanges,
        t('race.lineup.combinedLabel', 'Combined lineup'),
      ),
    ];
  }, [highlightRanges, locale, t, trackData]);

  const maneuverWindows = useMemo<TimeWindow[]>(() => {
    if (!excludeManeuvers || tacks.length === 0 || trackData.length === 0) {
      return [];
    }
    const windows: TimeWindow[] = [];
    for (const tack of tacks) {
      const startGlobal = (tack as Partial<{ start_index: number; startIndex: number }>).start_index ??
        (tack as Partial<{ startIndex: number }>).startIndex;
      const endGlobal = (tack as Partial<{ end_index: number; endIndex: number }>).end_index ??
        (tack as Partial<{ endIndex: number }>).endIndex;
      const startIndex = translateGlobalIndex(startGlobal);
      const endIndex = translateGlobalIndex(endGlobal);
      if (startIndex == null || endIndex == null) {
        continue;
      }
      const startPoint = trackData[startIndex];
      const endPoint = trackData[endIndex];
      if (!startPoint?.utc || !endPoint?.utc) {
        continue;
      }
      const startMs = Date.parse(startPoint.utc) - MANEUVER_PADDING_MS;
      const endMs = Date.parse(endPoint.utc) + MANEUVER_PADDING_MS;
      if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
        windows.push({ startMs, endMs });
      }
    }
    return mergeWindows(windows);
  }, [excludeManeuvers, tacks, trackData, translateGlobalIndex]);

  const comparisonOptions = useMemo(() => {
    return relatedTrackLayers.map((layer) => ({
      value: layer.selectionKey ?? `${layer.fileId}`,
      label: layer.label,
      color: layer.color || SECONDARY_COLOR,
      dataset: layer.trackData,
    }));
  }, [relatedTrackLayers]);

  useEffect(() => {
    if (selectedComparisonKey && !comparisonOptions.some((option) => option.value === selectedComparisonKey)) {
      setSelectedComparisonKey(null);
    }
  }, [selectedComparisonKey, comparisonOptions]);

  const comparisonLayer = selectedComparisonKey
    ? comparisonOptions.find((option) => option.value === selectedComparisonKey)
    : null;

  const groupedSummaries = useMemo(() => {
    return rangeGroups.map((group) => {
      const primarySamples = collectPrimarySamples(trackData, group.indexRanges, maneuverWindows);
      const primaryFirstSample = primarySamples[0];
      const primaryLastSample = primarySamples.at(-1);
      const primarySummary: BoatSummary = {
        id: 'primary',
        label: primaryBoatName || t('race.lineup.primaryBoat', 'Primary boat'),
        color: PRIMARY_COLOR,
        samples: primarySamples,
        stats: {},
        distanceSailed: computeTrackDistance(primarySamples),
        straightLineDistance: computeStraightLine(primarySamples),
        durationMs:
          primarySamples.length >= 2 && primaryFirstSample && primaryLastSample
            ? Math.max(0, primaryLastSample.timestamp - primaryFirstSample.timestamp)
            : 0,
        avgHeading: averageAngle(primarySamples, 'hdg') ?? averageAngle(primarySamples, 'cog'),
        avgTwd: averageAngle(primarySamples, 'twd'),
      };
      for (const metric of selectedMetrics) {
        primarySummary.stats[metric] = computeMetricStats(metric, primarySamples);
      }

      let comparisonSummary: BoatSummary | null = null;
      if (comparisonLayer) {
        const overlaySamples = collectOverlaySamples(
          comparisonLayer.dataset,
          group.timeWindows,
          maneuverWindows,
        );
        const overlayFirstSample = overlaySamples[0];
        const overlayLastSample = overlaySamples.at(-1);
        comparisonSummary = {
          id: comparisonLayer.value,
          label: comparisonLayer.label,
          color: comparisonLayer.color,
          samples: overlaySamples,
          stats: {},
          distanceSailed: computeTrackDistance(overlaySamples),
          straightLineDistance: computeStraightLine(overlaySamples),
          durationMs:
            overlaySamples.length >= 2 && overlayFirstSample && overlayLastSample
              ? Math.max(0, overlayLastSample.timestamp - overlayFirstSample.timestamp)
              : 0,
          avgHeading:
            averageAngle(overlaySamples, 'hdg') ?? averageAngle(overlaySamples, 'cog'),
          avgTwd: averageAngle(overlaySamples, 'twd'),
        };
        for (const metric of selectedMetrics) {
          comparisonSummary.stats[metric] = computeMetricStats(metric, overlaySamples);
        }
      }

      return {
        group,
        primary: primarySummary,
        comparison: comparisonSummary,
        gain: comparisonSummary ? computeGainSummary(primarySummary, comparisonSummary) : null,
      };
    });
  }, [
    rangeGroups,
    trackData,
    maneuverWindows,
    primaryBoatName,
    t,
    selectedMetrics,
    comparisonLayer,
  ]);

  const renderValue = (summary: BoatSummary, metric: string, variant: 'avg' | 'min' | 'max' | 'std') => {
    const stats = summary.stats[metric];
    if (!stats) {
      return <span className="lineup-value">—</span>;
    }
    const valueMap: Record<typeof variant, number | null> = {
      avg: stats.avg,
      min: stats.min,
      max: stats.max,
      std: stats.stdDev,
    };
    const displayValue = valueMap[variant];
    if (displayValue === null || Number.isNaN(displayValue)) {
      return <span className="lineup-value">—</span>;
    }
    return (
      <span className="lineup-value" style={{ color: summary.color }}>
        {numberFormatter.format(displayValue)}
      </span>
    );
  };

  if (trackData.length === 0) {
    return (
      <div className="lineup-panel theme-inverted">
        <div className="lineup-empty">
          <Text fw={600}>{t('race.lineup.emptyTitle', 'No track loaded')}</Text>
          <Text c="dimmed" size="sm">
            {t('race.lineup.emptySubtitle', 'Select a race to view lineup statistics.')}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="lineup-panel theme-inverted">
      <ScrollArea className="lineup-scroll" type="hover">
        <Stack gap="md">
          <Card padding="md" radius="lg" className="lineup-card">
            <Stack gap="md">
              <Group justify="space-between" align="center" className="lineup-header">
                <Title order={4}>{t('race.lineup.controls.title', 'Lineup controls')}</Title>
                <Button size="xs" variant="light" onClick={() => setSettingsOpen((previous) => !previous)}>
                  {settingsOpen
                    ? t('race.lineup.controls.hideSettings', 'Hide settings')
                    : t('race.lineup.controls.showSettings', 'Show settings')}
                </Button>
              </Group>

              {settingsOpen && (
                <Stack gap="sm">
                  <MultiSelect
                    label={t('race.lineup.controls.metrics', 'Metrics')}
                    placeholder={t('race.lineup.controls.metricsPlaceholder', 'Select metrics')}
                    data={availableMetrics.map((metric) => ({
                      value: metric,
                      label: formatMetricLabel(metric),
                    }))}
                    value={selectedMetrics}
                    onChange={setSelectedMetrics}
                    searchable
                    clearable
                  />
                  <Select
                    label={t('race.lineup.controls.comparison', 'Comparison boat')}
                    placeholder={t('race.lineup.controls.comparisonPlaceholder', 'Select boat')}
                    clearable
                    data={comparisonOptions}
                    value={selectedComparisonKey}
                    onChange={setSelectedComparisonKey}
                  />
                  <Group gap="lg" wrap="wrap">
                    <Switch
                      styles={{
                        root: { width: '100%' },
                        body: { justifyContent: 'space-between', width: '100%' },
                      }}
                      checked={excludeManeuvers}
                      onChange={(event) => setExcludeManeuvers(event.currentTarget.checked)}
                      label={t('race.lineup.controls.excludeManeuvers', 'Exclude maneuvers')}
                      description={t('race.lineup.controls.excludeHint', {
                        seconds: MANEUVER_PADDING_SECONDS,
                      })}
                    />
                  </Group>
                </Stack>
              )}
            </Stack>
          </Card>

          {groupedSummaries.length === 0 && (
            <Card padding="lg" radius="lg" className="lineup-card">
              <Stack gap="xs">
                <Text fw={600}>{t('race.lineup.noSegments', 'No lineup range selected')}</Text>
                <Text c="dimmed" size="sm">
                  {t(
                    'race.lineup.noSegmentsHint',
                    'Highlight a time range on the line chart to focus the lineup analysis.',
                  )}
                </Text>
              </Stack>
            </Card>
          )}

          {groupedSummaries.map(({ group, primary, comparison, gain }) => (
            <Card key={group.id} padding="lg" radius="lg" className="lineup-card">
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text fw={600}>{group.label}</Text>
                    <Text size="sm" c="dimmed">
                      {group.description}
                    </Text>
                  </div>
                  <Badge radius="sm" variant="light">
                    {group.durationLabel}
                  </Badge>
                </Group>

                <div className="lineup-table-wrapper">
                  <Table>
                    <thead>
                      <tr>
                        <th>{t('race.lineup.table.metric', 'Metric')}</th>
                        <th>{t('race.lineup.table.avg', 'Avg')}</th>
                        <th>{t('race.lineup.table.min', 'Min')}</th>
                        <th>{t('race.lineup.table.max', 'Max')}</th>
                        <th>{t('race.lineup.table.std', 'Std dev')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMetrics.map((metric) => (
                        <tr key={`${group.id}-${metric}`}>
                          <td>
                            <Text fw={600}>{formatMetricLabelNoUnit(metric)}</Text>
                            <Text size="xs" c="dimmed">
                              {t('race.lineup.table.samples', '{{count}} samples', {
                                count: primary.stats[metric]?.count ?? 0,
                              })}
                            </Text>
                          </td>
                          <td>
                            <div className="lineup-value-stack">
                              {renderValue(primary, metric, 'avg')}
                              {comparison && renderValue(comparison, metric, 'avg')}
                            </div>
                          </td>
                          <td>
                            <div className="lineup-value-stack">
                              {renderValue(primary, metric, 'min')}
                              {comparison && renderValue(comparison, metric, 'min')}
                            </div>
                          </td>
                          <td>
                            <div className="lineup-value-stack">
                              {renderValue(primary, metric, 'max')}
                              {comparison && renderValue(comparison, metric, 'max')}
                            </div>
                          </td>
                          <td>
                            <div className="lineup-value-stack">
                              {renderValue(primary, metric, 'std')}
                              {comparison && renderValue(comparison, metric, 'std')}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <Divider my="xs" variant="dashed" />

                <Stack gap="xs">
                  <Text fw={600}>{t('race.lineup.distanceTitle', 'Distance summary')}</Text>
                  <Table className="lineup-distance-table">
                    <tbody>
                      <tr>
                        <th>{t('race.lineup.distanceSailed', 'Distance sailed [m]')}</th>
                        <td style={{ color: primary.color }}>
                          {distanceFormatter.format(primary.distanceSailed)}
                        </td>
                        <td style={{ color: comparison?.color }}>
                          {comparison ? distanceFormatter.format(comparison.distanceSailed) : '—'}
                        </td>
                      </tr>
                      <tr>
                        <th>{t('race.lineup.distanceStraight', 'Start–end straight line [m]')}</th>
                        <td style={{ color: primary.color }}>
                          {distanceFormatter.format(primary.straightLineDistance)}
                        </td>
                        <td style={{ color: comparison?.color }}>
                          {comparison ? distanceFormatter.format(comparison.straightLineDistance) : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Stack>

                {comparison ? (
                  gain ? (
                    <Stack gap="xs">
                      <Text fw={600}>
                        {t('race.lineup.gainTitle', {
                          primary: primary.label,
                          secondary: comparison.label,
                        })}
                      </Text>
                      <Table className="lineup-gain-table">
                        <thead>
                          <tr>
                            <th />
                            <th>{t('race.lineup.gainForward', 'Forward')}</th>
                            <th>{t('race.lineup.gainUp', 'Up')}</th>
                            <th>{t('race.lineup.gainVmg', 'VMG')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {([
                            ['begin', t('race.lineup.gainBegin', 'Begin')],
                            ['end', t('race.lineup.gainEnd', 'End')],
                            ['total', t('race.lineup.gainTotal', 'Gain (total)')],
                            ['perMinute', t('race.lineup.gainPerMinute', 'Gain per minute')],
                          ] as const).map(([key, label]) => (
                            <tr key={key}>
                              <th>{label}</th>
                              <td>{formatSigned(gain.forward[key], signedFormatter)}</td>
                              <td>{formatSigned(gain.up[key], signedFormatter)}</td>
                              <td>{formatSigned(gain.vmg[key], signedFormatter)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Stack>
                  ) : (
                    <Card radius="md" withBorder padding="sm" className="lineup-card muted">
                      <Text size="sm">
                        {t('race.lineup.gainUnavailable', 'Gain analysis unavailable for this selection.')}
                      </Text>
                    </Card>
                  )
                ) : (
                  <Card radius="md" withBorder padding="sm" className="lineup-card muted">
                    <Text size="sm">
                      {t(
                        'race.lineup.noComparisonSelected',
                        'Add an overlay boat to unlock the gain/loss breakdown.',
                      )}
                    </Text>
                  </Card>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      </ScrollArea>
    </div>
  );
};

function formatSigned(value: number | null, formatter: Intl.NumberFormat): string {
  if (value === null || Number.isNaN(value)) {
    return '—';
  }
  return formatter.format(value);
}

export default LineupPanel;
